import { createContext } from 'react'
import { LS_KEY } from '../constants'

export interface IAuthInfo {
  id_token: string,
  access_token: string,
  email: string,
  refresh_token?: string,
}

export const AuthContext = createContext<Partial<IAuthInfo> & { loading: boolean }>({ loading: false })

export function getAuthInfoFromLocalStorage(): IAuthInfo | null {
  let info: IAuthInfo | null = null;
  try {
    const authInfoString = localStorage.getItem(LS_KEY.AUTH_INFO);
    if (authInfoString) {
      const value = JSON.parse(authInfoString);
      if ("id_token" in value && "access_token" in value && "email" in value) {
        info = value
      } else {
        console.error("Auth info in localStorage is wrong")
      }
    }
  } catch (err) {
    console.error(err);
  }
  return info;
}

// One shared SPA at this origin serves every instance, so a signed-in user goes
// to /portal rather than to the instance's own domain -- the instance is
// resolved from the token. Opt in with REACT_APP_USE_PORTAL=1; without it the
// classic per-instance behaviour is unchanged.
export const USE_PORTAL = process.env.REACT_APP_USE_PORTAL === "1";

export function launchMspPortal(domain: string, target: "_blank" | "_self", bid?: string) {
  if (USE_PORTAL) {
    // The caller's own instance: /portal resolves it from the token, so no
    // Portal Id appears in the URL. /instance/list returns active instances
    // newest first, so a multi-instance owner typing /portal lands on their
    // most recent one.
    return go(bid ? `/portal/activate?bid=${bid}` : "/portal", target);
  }
  const url = domain.startsWith("http") ? domain : `https://${domain}`;
  window.open(bid ? `${url}/activate?bid=${bid}` : url, target)
}

/**
 * A specific instance, addressed by Portal Id -- for rows in the instance list,
 * where the caller is choosing between several.
 *
 * This is the only way to reach an instance the caller does not own: members and
 * support have nothing for /portal to resolve from a token, so the picker has to
 * name what it wants.
 */
export function launchInstancePortal(
  instance: { domain: string; label?: string },
  target: "_blank" | "_self",
  bid?: string
) {
  if (USE_PORTAL) {
    const portalId = instance.label || instance.domain.split(".")[0];
    const base = `/portals/${encodeURIComponent(portalId)}`;
    return go(bid ? `${base}/activate?bid=${bid}` : base, target);
  }
  launchMspPortal(instance.domain, target, bid);
}

function go(path: string, target: "_blank" | "_self") {
  if (target === "_self") window.location.assign(path);
  else window.open(path, target);
}

/**
 * Persist a session. The counterpart to clearSession: whatever is written here
 * must be removed there, or a half-cleared session survives a sign-out.
 *
 * AUTH_INFO is the legacy blob the landing page itself reads; the __*__ keys
 * are what the portal shells and the SPA read. Both are written until the
 * classic per-instance frontend is retired.
 */
export function storeSession(info: IAuthInfo) {
  localStorage.setItem(LS_KEY.AUTH_INFO, JSON.stringify(info));
  localStorage.setItem(LS_KEY.ID_TOKEN, info.id_token);
  localStorage.setItem(LS_KEY.ACCESS_TOKEN, info.access_token);
  if (info.refresh_token) {
    localStorage.setItem(LS_KEY.REFRESH_TOKEN, info.refresh_token);
  }
  // How to renew them, recorded by whoever issued them.
  localStorage.setItem(LS_KEY.AUTH_CLIENT, process.env.REACT_APP_CLIENT_ID || "");
  localStorage.setItem(LS_KEY.AUTH_REGION, process.env.REACT_APP_COGNITO_REGION || "");
}

/**
 * Remove every trace of the session from this origin.
 *
 * A session is not just AUTH_INFO any more: the portal keys are what the shared
 * SPA reads, and the fw-fe* cookies pin which frontend version it is served.
 * Clearing only AUTH_INFO leaves the portal signed in, which is why every
 * sign-out path must go through here rather than removing keys itself.
 */
export function clearSession() {
  [LS_KEY.AUTH_INFO, LS_KEY.ID_TOKEN, LS_KEY.ACCESS_TOKEN, LS_KEY.REFRESH_TOKEN,
   LS_KEY.AUTH_CLIENT, LS_KEY.AUTH_REGION]
    .forEach((k) => localStorage.removeItem(k));
  // Version cookies are path-scoped per mount, so each has to be expired on the
  // path it was set on -- Path=/ would not match them.
  document.cookie.split(";").forEach((c) => {
    const name = c.split("=")[0].trim();
    // "fw-fe" is the own-instance cookie; "fw-fe-{portal-id}" a named one. Test
    // for both -- a startsWith("fw-fe-") check silently skips the own-instance one.
    if (name !== "fw-fe" && !name.startsWith("fw-fe-")) return;
    const portalId = name === "fw-fe" ? "" : name.slice("fw-fe-".length);
    const paths = portalId ? ["/portal", `/portals/${portalId}`, "/"] : ["/portal", "/"];
    paths.forEach((path) => {
      document.cookie = `${name}=; Path=${path}; Max-Age=0; SameSite=Lax`;
    });
  });
}

export function getLoginServerFromLocalStorage(): string {
  let loginServer = '';
  try {
    loginServer = localStorage.getItem(LS_KEY.LOGIN_SERVER) || process.env.REACT_APP_LOGIN_SERVER || '';
  } catch (err) {
    console.error(err);
  }
  return loginServer;
}